import React, { useState } from 'react';
import { AlertTriangle, RefreshCw, Trash2, FileText, Clock } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/card';

interface DeadLetterMessage {
  id: string;
  originalTimestamp: string;
  failureTimestamp: string;
  retryCount: number;
  errorMessage: string;
  payload: any;
}

interface DeadLetterQueueProps {
  messageCount: number;
  messages: DeadLetterMessage[];
  onRetry: (messageId: string) => void;
  onRetryAll: () => void;
  onPurge: (messageId: string) => void;
  onPurgeAll: () => void;
}

export const DeadLetterQueue: React.FC<DeadLetterQueueProps> = ({
  messageCount,
  messages,
  onRetry,
  onRetryAll,
  onPurge,
  onPurgeAll
}) => {
  const [selectedMessage, setSelectedMessage] = useState<string | null>(null);
  const [showConfirmPurgeAll, setShowConfirmPurgeAll] = useState(false);

  const formatTimestamp = (timestamp: string): string => {
    const date = new Date(timestamp);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getTimeSince = (timestamp: string): string => {
    const diff = Date.now() - new Date(timestamp).getTime();
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(hours / 24);
    
    if (days > 0) return `${days}d ago`;
    if (hours > 0) return `${hours}h ago`;
    return `${Math.floor(diff / 60000)}m ago`;
  };

  return (
    <Card className={messageCount > 0 ? 'border-red-200' : ''}>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <AlertTriangle className={`w-6 h-6 ${messageCount > 0 ? 'text-red-500' : 'text-gray-400'}`} />
            <div>
              <CardTitle>Dead Letter Queue</CardTitle>
              <p className="text-sm text-gray-600 mt-1">
                {messageCount} message{messageCount !== 1 ? 's' : ''} failed to process
              </p>
            </div>
          </div>
          {messageCount > 0 && (
            <div className="flex space-x-2">
              <button
                onClick={onRetryAll}
                className="px-3 py-1.5 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors flex items-center space-x-1"
              >
                <RefreshCw className="w-4 h-4" />
                <span>Retry All</span>
              </button>
              <button
                onClick={() => setShowConfirmPurgeAll(true)}
                className="px-3 py-1.5 bg-red-600 text-white rounded hover:bg-red-700 transition-colors flex items-center space-x-1"
              >
                <Trash2 className="w-4 h-4" />
                <span>Purge All</span>
              </button>
            </div>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {messageCount === 0 ? (
          <div className="text-center py-8">
            <AlertTriangle className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-600">No messages in dead letter queue</p>
            <p className="text-sm text-gray-500 mt-1">
              All messages are being processed successfully
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {messages.map((message) => (
              <div
                key={message.id}
                className="border rounded-lg p-4 hover:bg-gray-50 transition-colors"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-2">
                      <FileText className="w-4 h-4 text-gray-400" />
                      <span className="font-mono text-sm text-gray-600">
                        {message.id.substring(0, 8)}...
                      </span>
                      <span className="text-sm text-gray-500">
                        • {message.retryCount} retries
                      </span>
                    </div>
                    <div className="text-sm text-red-600 mb-2">
                      {message.errorMessage}
                    </div>
                    <div className="flex items-center space-x-4 text-xs text-gray-500">
                      <div className="flex items-center space-x-1">
                        <Clock className="w-3 h-3" />
                        <span>Failed {getTimeSince(message.failureTimestamp)}</span>
                      </div>
                      <span>•</span>
                      <span>Originally sent {formatTimestamp(message.originalTimestamp)}</span>
                    </div>
                  </div>
                  <div className="flex space-x-2 ml-4">
                    <button
                      onClick={() => onRetry(message.id)}
                      className="p-2 text-blue-600 hover:bg-blue-50 rounded transition-colors"
                      title="Retry message"
                    >
                      <RefreshCw className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => onPurge(message.id)}
                      className="p-2 text-red-600 hover:bg-red-50 rounded transition-colors"
                      title="Delete message"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
                
                {selectedMessage === message.id && (
                  <div className="mt-3 pt-3 border-t">
                    <p className="text-xs font-medium text-gray-600 mb-1">Message Payload:</p>
                    <pre className="text-xs bg-gray-100 p-2 rounded overflow-x-auto">
                      {JSON.stringify(message.payload, null, 2)}
                    </pre>
                  </div>
                )}
                
                <button
                  onClick={() => setSelectedMessage(
                    selectedMessage === message.id ? null : message.id
                  )}
                  className="text-xs text-blue-600 hover:text-blue-800 mt-2"
                >
                  {selectedMessage === message.id ? 'Hide' : 'Show'} payload
                </button>
              </div>
            ))}
          </div>
        )}

        {showConfirmPurgeAll && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 max-w-md">
              <h3 className="text-lg font-semibold mb-3">Confirm Purge All</h3>
              <p className="text-gray-600 mb-4">
                Are you sure you want to permanently delete all {messageCount} messages from the dead letter queue? 
                This action cannot be undone.
              </p>
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => setShowConfirmPurgeAll(false)}
                  className="px-4 py-2 border border-gray-300 rounded hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    onPurgeAll();
                    setShowConfirmPurgeAll(false);
                  }}
                  className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 transition-colors"
                >
                  Purge All Messages
                </button>
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};